import { parseDbTimestamp } from "@repo/shared";
import type { ApprovalStatus } from "@repo/shared/types";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useNavigate } from "react-router";
import { PageHeader } from "@/components/page-header";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/ui/status-badge";
import {
  Table,
  TableBody,
  TableCell,
  TableCellLink,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTabParam } from "@/hooks/use-tab-param";
import { getApprovalQueue } from "@/lib/api/compliance";
import { useMe } from "@/lib/hooks/use-clients";
import { useT } from "@/lib/i18n/use-t";
import { hasPermission, type Permission } from "@/lib/permissions";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { T } from "./page.i18n";

const FILTER_VALUES = ["todo", "approved", "rejected", "all"] as const;

export default function ComplianceApprovalsPage() {
  const navigate = useNavigate();
  const { formatDateTime } = useRegionalFormat();
  const { me, isLoading: meLoading } = useMe();
  const t = useT(T);
  const userPermissions = (me?.permissions || ["advisor"]) as Permission[];
  const [filter, setFilter] = useTabParam(FILTER_VALUES, "todo");

  useEffect(() => {
    if (!meLoading && !hasPermission(userPermissions, "compliance")) {
      navigate("/", { replace: true });
    }
  }, [meLoading, userPermissions, navigate]);

  const { data: queue, isLoading } = useQuery({
    queryKey: ["compliance", "approvals"],
    queryFn: getApprovalQueue,
    enabled: !meLoading && hasPermission(userPermissions, "compliance"),
  });

  if (meLoading || !hasPermission(userPermissions, "compliance")) return null;

  const statusLabel = (status: ApprovalStatus) =>
    (
      ({
        pending: t.statusPending,
        approved: t.statusApproved,
        rejected: t.statusRejected,
        withdrawn: t.statusWithdrawn,
      }) as Record<ApprovalStatus, string>
    )[status] || status;

  const items = (queue ?? []).filter((item) => {
    if (filter === "todo") return item.status === "pending";
    if (filter === "approved") return item.status === "approved";
    if (filter === "rejected") return item.status === "rejected";
    return true;
  });

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={[{ label: t.compliance, href: "/compliance" }, { label: t.flowApprovals }]}
        title={t.flowApprovals}
        description={t.description}
      />

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList>
          <TabsTrigger value="todo">{t.filterTodo}</TabsTrigger>
          <TabsTrigger value="approved">{t.filterApproved}</TabsTrigger>
          <TabsTrigger value="rejected">{t.filterRejected}</TabsTrigger>
          <TabsTrigger value="all">{t.filterAll}</TabsTrigger>
        </TabsList>
      </Tabs>

      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : items.length === 0 ? (
        <EmptyState>{t.empty}</EmptyState>
      ) : (
        <Table variant="navigation">
          <TableHeader>
            <TableRow>
              <TableHead>{t.client}</TableHead>
              <TableHead>{t.flow}</TableHead>
              <TableHead>{t.requested}</TableHead>
              <TableHead>{t.requestedBy}</TableHead>
              <TableHead>{t.status}</TableHead>
              <TableHead>{t.decided}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.approvalId}>
                <TableCellLink
                  className="font-medium"
                  to={`/clients/${item.clientId}/flows/${item.flowInstanceId}/process`}
                >
                  {item.clientName}
                </TableCellLink>
                <TableCell>{item.flowInstanceName || item.flowName}</TableCell>
                <TableCell>{formatDateTime(parseDbTimestamp(item.requestedAt))}</TableCell>
                <TableCell>{item.requestedByName || "—"}</TableCell>
                <TableCell>
                  <StatusBadge status={item.status}>{statusLabel(item.status)}</StatusBadge>
                </TableCell>
                <TableCell>
                  {item.decidedAt ? (
                    <span>
                      {formatDateTime(parseDbTimestamp(item.decidedAt))}
                      {item.decidedByName ? (
                        <span className="text-muted-foreground"> · {item.decidedByName}</span>
                      ) : null}
                    </span>
                  ) : (
                    "—"
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
